<template>
    <div
        class="mx-4"
    >
        <h3
            ref="elRatingsTitle"
            class="py-0 mt-0 mb-4 text-uppercase text-center text-md-start"
        >
            Ratings &amp; Reviews
        </h3>

        <!-- Spinner -->
        <div
            v-if="['idle', 'pending'].includes(reviewLoadingStatus)"
            class="d-flex align-center justify-center"
        >
            <v-progress-circular
                size="42"
                color="accent"
                indeterminate
            ></v-progress-circular>
        </div>
        <template
            v-else-if="!lo_isError(reviewsData) && reviewsData?.summary"
        >
            <ProductRatingSummary
                :summary="reviewsData.summary"
            >
                <template #button>
                    <v-btn
                        @click="onToggleForm()"
                        color="secondary"
                        variant="flat"
                        size="small"
                    >
                        {{ writeReviewBtnLabel }}
                    </v-btn>
                </template>
            </ProductRatingSummary>
            <template
                v-if="reviewsData.reviews?.length"
            >
                <div
                    class="mt-10"
                >
                    <v-divider
                        class="my-3"
                    ></v-divider>
                    <template
                        v-for="(review, index) in reviewsData.reviews"
                    >
                        <ProductReviewsListItem
                            :review="review"
                            :reply-name="brandTitle"
                        ></ProductReviewsListItem>
                        <v-divider
                            class="my-3"
                        ></v-divider>
                    </template>

                    <v-pagination
                        v-if="pagination.totalPages > 1"
                        v-model="pagination.page"
                        :length="pagination.totalPages"
                        @update:modelValue="onPageChange($event)"
                    ></v-pagination>
                </div>
            </template>
        </template>
        <template
            v-else
        >
            <v-alert
                type="warning"
            >
                Unable to load Reviews. Please reload page to try again.
            </v-alert>
        </template>

        <ResponsiveModal v-model="dialogForm">
            <template #default="{ onExit, openCount }">
                <ProductReviewsForm
                    v-model="reviewModel"
                    :key="`review-form-open-${openCount}`"
                    :in-modal="true"
                    :progress="isFormProgress"
                    @update:model-value="onSave($event)"
                    @on-exit="onExit(), onReset()"
                ></ProductReviewsForm>
            </template>
        </ResponsiveModal>
    </div>
</template>

<script lang="ts" setup>
    import type {
        Review,
        ReviewPreForm,
        ReviewsResponse
    } from '~/types';
    import type {
        PaginatedRequest
    } from 'x1-base/types';
    import {
        useReview,
        useApiQueryParams
    } from '~/composables';
    import { useGlobalAlert } from 'x1-base/composables';
    import { isError as lo_isError } from 'es-toolkit';
    import { clone as lo_clone } from 'es-toolkit';
    import ProductRatingSummary from '~/components/products/reviews/ProductRatingSummary/ProductRatingSummary.vue';
    import ProductReviewsListItem from '~/components/products/reviews/ProductReviewsListItem/ProductReviewsListItem.vue';
    import { nameToFirstLastInitials } from 'x1-base/utils';

    const { getReviews, createReview } = useReview();
    const ProductReviewsForm = defineAsyncComponent(() => import('~/components/products/reviews/ProductReviewsForm/ProductReviewsForm.vue'));

    const props = defineProps<{
        productUid: string;
        skuUid: Nilish<string>;
    }>();

    const {
        pagination,
        updatePagination,
        getApiQueryParams
    } = useApiQueryParams({
        key: `ProdReviewQParams`,
        resetOnUnload: true,
        opts: {
            page_size: 5
        },
        persistedParams:{
            product: props.productUid,
            ordering: '-date'
        }
    });

    const $config = useRuntimeConfig();
    const brandTitle = $config.public.brand.title as string;

    const nuxtApp = useNuxtApp();
    const { userObj } = nuxtApp.$user;

    const elRatingsTitle = shallowRef<HTMLElement>();
    const dialogForm = ref(false);
    const isFormProgress = ref(false);
    const createGlobalAlert = useGlobalAlert();

    const reviewBaseModel: ReviewPreForm = {
        product: null,
        sku: null,
        rating: null,
        title: null,
        content: null,
        author: getAuthorName(),
        email: userObj.value?.email ?? null,
        invite: null
    };
    const reviewModel = ref<ReviewPreForm>(lo_clone(reviewBaseModel));

    async function getItems(): Promise<ReviewsResponse | Error> {
        const paginatedRequest: PaginatedRequest = {
            params: getApiQueryParams()
        };

        const response = await getReviews(paginatedRequest);

        if (lo_isError(response)) {
            return response;
        }

        const [ reviews, summary ] = response;
        updatePagination(reviews.pagination);
        return { reviews: reviews.data, summary };
    }

    const { data: reviewsData, refresh: onReloadList, status: reviewLoadingStatus } = useLazyAsyncData<ReviewsResponse | Error>(
        `keyReviewsItems`,
        () => getItems(),
        {
            server: false
        }
    );

    const writeReviewBtnLabel = computed(() => {
        const isFirst = (!lo_isError(reviewsData.value) && !reviewsData.value?.summary.total);
        return `Write ${isFirst ? 'The First' : 'A'} Review`;
    });

    function onToggleForm(): void {
        dialogForm.value = !dialogForm.value;
    }

    async function onSave(reviewObj: Review): Promise<void> {
        const payload = Object.assign(
            {},
            reviewModel.value,
            reviewObj,
            { product: props.productUid }
        );

        isFormProgress.value = true;
        const response = await createReview(payload);
        isFormProgress.value = false;

        if (lo_isError(response)) {
            createGlobalAlert({
                text: 'Unable to save review. Please try again.',
                show: true,
                type: 'error'
            });
            return;
        }

        onToggleForm();
        onReset();

        createGlobalAlert({
            text: 'Thank You For Your Valuable Feedback. We will process your review and publish it soon.',
            show: true,
            type: 'success'
        });
    }

    function onReset(): void {
        reviewModel.value = lo_clone(reviewBaseModel);
    }

    function getAuthorName(): Nullable<string> {
        if (userObj.value) {
            const { label, firstName, lastName } = userObj.value;
            const name: string | string[] = label || [firstName || '', lastName || ''];
            return nameToFirstLastInitials(name, 'Anon.') || null;
        }

        return null;
    }

    async function onPageChange(page: number): Promise<void> {
        const top = elRatingsTitle.value?.offsetTop;
        updatePagination({ page });

        if (top) {
            window.scrollTo({
                top,
                behavior: 'instant'
            });
        }

        await nextTick();
        onReloadList();
    }
</script>

<style lang="scss"></style>
